

export interface TestReviewFeedback {
  test_file: string;
  evaluator: string;
  timestamp: string;
  question: string;
  generated_answer: string;
  category: 'validating_positives' | 'validating_negatives' | 'knowledge_base';
  feedback: 'agree' | 'disagree' | 'confirm_kb_issue' | 'not_kb_issue' | 'answer_satisfactory';
  notes: string;
}

export interface ReviewProgress {
  test_file: string;
  evaluator: string;
  validating_positives_completed: number;
  validating_negatives_completed: number;
  knowledge_base_completed: number;
  total_kb_issues: number;
  is_complete: boolean;
  last_updated: string;
}

/**
 * Service for managing test review feedback and progress tracking
 * Stores data in Azure Table Storage: https://unifirstailabstacct1.table.core.windows.net/AgentTestFeedbackHR
 */
class TestReviewService {
  private apiBaseUrl: string;

  constructor() {
    // Use centralized app configuration for API base URL
    // Ensure endpoints match Express mounting in server.js: app.use('/api/testing', testReviewFeedbackRouter);
    // So requests should go to `${baseUrl}/api/testing/...`
    const { appConfig } = require('../config/appConfig');
    this.apiBaseUrl = `${appConfig.api.baseUrl}/api`;
  }

  /**
   * Submit feedback for a test result
   */
  async submitFeedback(feedback: TestReviewFeedback): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/testing/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedback)
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to submit test review feedback:', error);
      throw error;
    }
  }

  /**
   * Get user's progress for a specific test file
   */
  async getReviewProgress(testFile: string, evaluatorEmail: string): Promise<ReviewProgress | null> {
    try {
      const encodedTestFile = encodeURIComponent(testFile);
      const encodedEvaluator = encodeURIComponent(evaluatorEmail);
      
      const response = await fetch(
        `${this.apiBaseUrl}/testing/feedback/progress?test_file=${encodedTestFile}&evaluator=${encodedEvaluator}`
      );

      if (response.status === 404) {
        return null; // No progress found
      }

      if (!response.ok) {
        throw new Error(`Failed to get review progress: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get review progress:', error);
      throw error;
    }
  }

  /**
   * Get all feedback submitted by a user for a specific test file
   */
  async getUserFeedback(testFile: string, evaluatorEmail: string): Promise<TestReviewFeedback[]> {
    try {
      const encodedTestFile = encodeURIComponent(testFile);
      const encodedEvaluator = encodeURIComponent(evaluatorEmail);
      
      const response = await fetch(
        `${this.apiBaseUrl}/testing/feedback?test_file=${encodedTestFile}&evaluator=${encodedEvaluator}`
      );

      if (!response.ok) {
        throw new Error(`Failed to get user feedback: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get user feedback:', error);
      throw error;
    }
  }

  /**
   * Calculate review progress from feedback entries
   */
  calculateProgressFromFeedback(feedback: TestReviewFeedback[], totalKbIssues: number): ReviewProgress {
    const positivesCompleted = feedback.filter(f => f.category === 'validating_positives').length;
    const negativesCompleted = feedback.filter(f => f.category === 'validating_negatives').length;
    const kbCompleted = feedback.filter(f => f.category === 'knowledge_base').length;

    const isComplete = positivesCompleted >= 5 && negativesCompleted >= 5 && kbCompleted >= totalKbIssues;

    return {
      test_file: feedback[0]?.test_file || '',
      evaluator: feedback[0]?.evaluator || '',
      validating_positives_completed: positivesCompleted,
      validating_negatives_completed: negativesCompleted,
      knowledge_base_completed: kbCompleted,
      total_kb_issues: totalKbIssues,
      is_complete: isComplete,
      last_updated: new Date().toISOString()
    };
  }

  /**
   * Check if user has completed all review tasks for a test file
   */
  async isReviewComplete(testFile: string, evaluatorEmail: string, totalKbIssues: number): Promise<boolean> {
    try {
      const feedback = await this.getUserFeedback(testFile, evaluatorEmail);
      const progress = this.calculateProgressFromFeedback(feedback, totalKbIssues);
      return progress.is_complete;
    } catch (error) {
      console.error('Failed to check review completion:', error);
      return false;
    }
  }

  /**
   * Get the current phase and position for a user's review
   */
  async getReviewPosition(testFile: string, evaluatorEmail: string, totalKbIssues: number): Promise<{
    phase: 'validating_positives' | 'validating_negatives' | 'knowledge_base' | 'complete';
    position: number;
    total: number;
  }> {
    try {
      const feedback = await this.getUserFeedback(testFile, evaluatorEmail);
      const progress = this.calculateProgressFromFeedback(feedback, totalKbIssues);

      if (progress.validating_positives_completed < 5) {
        return {
          phase: 'validating_positives',
          position: progress.validating_positives_completed,
          total: 5
        };
      }

      if (progress.validating_negatives_completed < 5) {
        return {
          phase: 'validating_negatives',
          position: progress.validating_negatives_completed,
          total: 5
        };
      }

      if (progress.knowledge_base_completed < totalKbIssues) {
        return {
          phase: 'knowledge_base',
          position: progress.knowledge_base_completed,
          total: totalKbIssues
        };
      }

      return {
        phase: 'complete',
        position: totalKbIssues,
        total: totalKbIssues
      };
    } catch (error) {
      console.error('Failed to get review position:', error);
      return {
        phase: 'validating_positives',
        position: 0,
        total: 5
      };
    }
  }

  /**
   * Delete all feedback entries for a specific user and test file
   */
  async deleteUserFeedback(testFile: string, evaluator: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/testing/feedback/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          test_file: testFile,
          evaluator: evaluator
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to delete feedback: ${response.status} ${response.statusText}`);
      }
      
      console.log(`✅ Successfully deleted feedback for ${evaluator} on ${testFile}`);
    } catch (error) {
      console.error('Failed to delete user feedback:', error);
      throw new Error(`Failed to delete feedback: ${error}`);
    }
  }
}

export const testReviewService = new TestReviewService();